/**
 * AAMI ST91 Compliance Resource Page — B2B SEO magnet.
 *
 * Targets high-intent endoscopy / SPD queries:
 *   • "AAMI ST91 flexible endoscope reprocessing"
 *   • "ST91 endoscope water quality"
 *   • "AAMI ST91 vs ST79"
 *   • "endoscope reprocessing survey findings"
 *
 * Structured as a TechnicalArticle so Google indexes it as authoritative
 * technical content for endoscopy managers, SPD directors, and infection
 * preventionists.
 */
import SEO, { technicalArticleSchema } from "@/components/SEO";
import SiteHeader from "@/components/SiteHeader";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { CheckCircle2, Droplets, FileText, ShieldCheck, ArrowRight, AlertTriangle, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";

const KEY_REQUIREMENTS = [
  {
    area: "Water quality",
    detail: "Critical water (per ST108 Category 2) for terminal rinse; verified TOC, conductivity, and endotoxin limits.",
  },
  {
    area: "Manual cleaning",
    detail: "Bedside pre-treatment within manufacturer-defined window (typically < 1 hr); documented brush sizes and channel-flush volumes per scope model.",
  },
  {
    area: "Storage & drying",
    detail: "Vertical hang or HEPA-filtered drying cabinets; documented drying-cycle validation; storage duration limits based on facility risk assessment.",
  },
  {
    area: "Staff competency",
    detail: "Initial + annual competency validation for every scope model reprocessed. Records must be produceable within 24 hours of survey request.",
  },
  {
    area: "Quality monitoring",
    detail: "Documented daily leak tests, weekly ATP/protein or borescope inspections, and quarterly microbial cultures per scope family.",
  },
  {
    area: "Traceability",
    detail: "Scope-to-patient linkage: which endoscope was used on which patient, with reprocessing operator and cycle data captured electronically.",
  },
];

const FAQS = [
  {
    q: "What is AAMI ST91?",
    a: "ANSI/AAMI ST91:2021 — 'Flexible and semi-rigid endoscope processing in health care facilities' — is the U.S. consensus standard for reprocessing flexible endoscopes (GI, bronchoscopes, cystoscopes, ENT scopes, etc.). It defines cleaning, high-level disinfection or sterilization, drying, storage, staff competency, and quality-monitoring requirements. It replaced ST58's endoscope content and consolidates prior TIR reports.",
  },
  {
    q: "How does ST91 relate to ST108?",
    a: "ST91 defines endoscope reprocessing workflow; ST108 defines the water used at every stage of that workflow. Terminal-rinse water for flexible endoscopes must meet ST108 Category 2 (Critical Water) limits. A facility cannot be ST91-compliant without ST108-compliant water.",
  },
  {
    q: "How is ST91 different from ST79?",
    a: "ST79 covers steam sterilization of reusable medical devices (surgical instruments, rigid endoscopes, etc.). ST91 covers flexible + semi-rigid endoscopes, which are typically high-level disinfected (not steam-sterilized). Many hospitals need both standards for a complete SPD compliance program.",
  },
  {
    q: "What are the most common ST91 survey findings?",
    a: "Top survey findings include: (1) missing terminal-rinse water quality documentation (ST108 Cat 2 gap), (2) inadequate drying-cycle validation, (3) incomplete staff competency records per scope model, (4) missing borescope or ATP monitoring, and (5) storage-duration policies not backed by facility risk assessment.",
  },
  {
    q: "How does Sterile Mate help facilities meet ST91?",
    a: "Sterile Mate provides ST108-compliant terminal-rinse water systems, borescope + ATP inspection kits, drying-cabinet HEPA replacements, and documented preventative-maintenance/compliance-audit binders. We serve endoscopy suites across Texas, Louisiana, Alabama, and Mississippi with same-day dispatch from Houston.",
  },
];

export default function AamiSt91Page() {
  const canonical = "/resources/aami-st91";
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      technicalArticleSchema({
        headline: "AAMI ST91 Flexible & Semi-Rigid Endoscope Reprocessing — 2024 Compliance Guide",
        description:
          "Complete ANSI/AAMI ST91:2021 endoscope reprocessing compliance guide. Water-quality requirements (ST108 Category 2), drying, storage, staff competency, monitoring, and common Joint Commission survey findings.",
        url: canonical,
        datePublished: "2026-01-22",
        dateModified: "2026-02-24",
        keywords: [
          "AAMI ST91",
          "AAMI ST91 flexible endoscope reprocessing",
          "ST91 endoscope water quality",
          "AAMI ST91 vs ST79",
          "endoscope reprocessing standards",
          "endoscope drying cabinet compliance",
          "bronchoscope reprocessing",
          "duodenoscope reprocessing",
        ],
      }),
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white" data-testid="aami-st91-page">
      <SEO
        canonical={canonical}
        title="AAMI ST91 Flexible Endoscope Reprocessing — 2024 Compliance Guide"
        description="Complete AAMI ST91:2021 compliance guide for flexible + semi-rigid endoscope reprocessing. Water quality (ST108 Cat 2), drying, storage, staff competency, and Gulf-region compliance services."
        keywords={[
          "AAMI ST91",
          "AAMI ST91 flexible endoscope reprocessing",
          "ST91 endoscope water quality",
          "endoscope reprocessing compliance",
        ]}
        schema={schema}
      />
      <SiteHeader />

      {/* HERO */}
      <section className="relative bg-gradient-to-br from-[#0a1a35] via-[#0b2447] to-[#0056A8] text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(34,211,238,0.18),transparent_60%)]" />
        <div className="relative max-w-6xl mx-auto px-6 py-20">
          <p className="text-[10px] font-bold tracking-[0.32em] uppercase text-cyan-300 mb-4">
            Compliance Resource · Endoscope Reprocessing
          </p>
          <h1 className="text-4xl md:text-6xl font-black leading-[1.05] max-w-4xl">
            AAMI ST91 Flexible &amp; Semi-Rigid Endoscope Reprocessing.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-white/85 max-w-3xl leading-relaxed">
            The 2024 compliance guide for flexible endoscope reprocessing — cleaning, high-level
            disinfection, terminal rinse (with <Link to="/resources/aami-st108" className="text-cyan-300 underline">ST108 Category 2</Link> water),
            drying, storage, staff competency, and quality monitoring. Serving endoscopy suites
            across <strong>Texas, Louisiana, Alabama, and Mississippi.</strong>
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold">
              <Link to="/marketplace/di-tank-exchange">
                <Droplets className="w-5 h-5 mr-2" />
                Critical-Water DI Tank Exchange
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
              <a href="mailto:info@sterilemate.com?subject=AAMI ST91 endoscopy compliance audit">
                <FileText className="w-5 h-5 mr-2" />
                Request Endoscopy Audit
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* KEY REQUIREMENTS */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-black text-slate-900 mb-2">Six Areas Every Endoscopy Suite Must Document</h2>
        <p className="text-slate-600 mb-8 max-w-2xl">
          ST91 organizes endoscope reprocessing into these six control areas. Joint Commission,
          DNV, and CMS surveyors will ask for documentation in each.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          {KEY_REQUIREMENTS.map((r) => (
            <div key={r.area} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                  <Activity className="w-5 h-5 text-blue-700" />
                </div>
                <div>
                  <p className="font-bold text-slate-900 mb-1">{r.area}</p>
                  <p className="text-sm text-slate-600 leading-relaxed">{r.detail}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-6 text-xs text-slate-500">
          Reference: ANSI/AAMI ST91:2021. Consult the full standard for complete requirements.
        </p>
      </section>

      {/* HOW WE HELP */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-black text-slate-900 mb-2">How Sterile Mate Supports ST91 Compliance</h2>
          <p className="text-slate-600 mb-8 max-w-2xl">
            One dispatch center in Houston serving endoscopy suites across the Gulf region — from
            major academic centers to critical-access hospitals.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                icon: Droplets,
                title: "Terminal-Rinse Water",
                text: "ST108 Category 2–compliant central RO + polishing loops sized for endoscopy suite peak demand. Includes ongoing endotoxin/TOC/conductivity testing.",
              },
              {
                icon: ShieldCheck,
                title: "Borescope + ATP Kits",
                text: "Weekly quality-monitoring supplies for every scope family: borescope inspection heads, ATP swabs, protein-detection swabs — all documented and audit-ready.",
              },
              {
                icon: CheckCircle2,
                title: "Drying & Storage",
                text: "HEPA-filter replacements for drying cabinets, storage-duration risk-assessment templates, and PM plans that match manufacturer IFUs.",
              },
            ].map((f) => (
              <div key={f.title} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <f.icon className="w-6 h-6 text-blue-700 mb-3" />
                <p className="font-bold text-slate-900 mb-1">{f.title}</p>
                <p className="text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-black text-slate-900 mb-8">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <details
              key={i}
              className="group rounded-xl border border-slate-200 bg-white p-5 open:shadow-md transition-shadow"
            >
              <summary className="cursor-pointer flex items-center justify-between font-bold text-slate-900">
                {f.q}
                <ArrowRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform" />
              </summary>
              <p className="mt-3 text-slate-700 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Warning banner */}
      <section className="bg-amber-50 border-y border-amber-200">
        <div className="max-w-4xl mx-auto px-6 py-8 flex items-start gap-4">
          <AlertTriangle className="w-6 h-6 text-amber-700 shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-amber-900 mb-1">
              Endoscope-borne infection outbreaks are climbing — surveyors are following.
            </p>
            <p className="text-sm text-slate-700 leading-relaxed">
              CDC and FDA have both issued advisories on duodenoscope- and bronchoscope-associated
              infections. Joint Commission is now regularly citing ST91 non-conformance under
              IC.02.02.01 (medical equipment reprocessing).{" "}
              <a href="mailto:info@sterilemate.com?subject=AAMI ST91 endoscopy compliance audit" className="font-semibold text-amber-800 underline">
                Request an endoscopy audit
              </a>{" "}
              — one-business-day turnaround.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
